import React, {
  FC,
  Fragment,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styles from "./edit-attributes-modal-view.module.scss";
import chevron from "../../images/chevron-20.svg";
import closeIcon from "../../images/close-16.svg";
import CustomScrollbar from "../../components/ui-kit/scrollbar/scrollbar";
import Input from "../../components/ui-kit/Input/input";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../hooks/useAppSelector";
import { useAppDispatch } from "../../hooks/useAppDispatch";
import { setConvertedFeatures } from "../../services/actions/map";
import { FieldInfo } from "../../interfaces/krookie";
import Dropdown from "../../components/ui-kit/dropdown/dropdown";
import MaskedInput from "../../components/ui-kit/masked-input/masked-input";
import {
  getDisplayValue,
  isDateType,
  processFieldValue,
  validateDate,
} from "./edit-attributes-modal-handlers";

interface EditAttributesModalViewProps {
  layerName: string;
  attributes: Record<string, string | number>;
  onSave?: (edited: any) => void;
  onCancel: () => void;
  userCanEditAttributes: boolean;
  fieldsInfo?: FieldInfo[] | null;
}

const deepClone = <T,>(obj: T): T =>
  typeof structuredClone === "function"
    ? structuredClone(obj)
    : JSON.parse(JSON.stringify(obj));

const shallowEqual = (a: Record<string, any>, b: Record<string, any>) => {
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  return aKeys.every((k) => a[k] === b[k]);
};

const EditAttributesModalView: FC<EditAttributesModalViewProps> = ({
  layerName,
  attributes,
  onSave,
  onCancel,
  userCanEditAttributes,
  fieldsInfo,
}) => {
  const { t } = useTranslation();

  const { drawingToolsIsActive } = useAppSelector((state) => state.map);
  const [editMode, setEditMode] = useState(false);
  const [editedAttributes, setEditedAttributes] = useState<Record<
    string,
    any
  > | null>(null);
  const [isCollapsed, setIsCollapsed] = useState<boolean>(false);
  const [attributeHistory, setAttributeHistory] = useState<
    Record<string, any>[]
  >([]);
  const dispatch = useAppDispatch();
  const inputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  useEffect(() => {
    setEditMode(false);
    setEditedAttributes(null);
    setAttributeHistory([]);
  }, [attributes]);

  const fieldsInfoMap = useMemo(() => {
    if (!fieldsInfo) return null;
    const map = new Map<string, FieldInfo>();
    fieldsInfo.forEach((field) => {
      map.set(field.fieldName, field);
    });
    return map;
  }, [fieldsInfo]);

  const hasChanges = useMemo(() => {
    if (!editedAttributes) return false;
    return Object.entries(attributes).some(
      ([key, val]) => editedAttributes[key] !== val,
    );
  }, [editedAttributes, attributes]);

  const pushToHistory = (snapshot: Record<string, any>) =>
    setAttributeHistory((stack) => {
      if (stack.length && shallowEqual(stack[stack.length - 1], snapshot))
        return stack;
      return [...stack, deepClone(snapshot)];
    });

  const handleKeyDown = (event: KeyboardEvent) => {
    const isUndo = event.ctrlKey && event.code === "KeyZ";

    if (isUndo && attributeHistory.length) {
      event.preventDefault();

      const prevSnapshot = attributeHistory[attributeHistory.length - 1];
      const curSnapshot = editedAttributes ?? attributes;

      const changedKey = Object.keys(prevSnapshot).find(
        (k) => prevSnapshot[k] !== curSnapshot[k],
      );

      setEditedAttributes(prevSnapshot);
      setAttributeHistory((stack) => stack.slice(0, stack.length - 1));

      setTimeout(() => {
        const input = changedKey && inputRefs.current[changedKey];
        if (input) {
          input.focus();
        }
      }, 0);
    }
  };

  useEffect(() => {
    if (!editMode) return;
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [editMode, handleKeyDown]);

  useEffect(() => {
    if (!drawingToolsIsActive && editMode) {
      setEditMode(false);
      setEditedAttributes(null);
      setAttributeHistory([]);
    }
  }, [drawingToolsIsActive, editMode]);

  const toggleCollapse = () => {
    setIsCollapsed((prev) => !prev);
  };

  const handleSave = () => {
    if (!editedAttributes || !hasChanges) {
      setEditMode(false);
      setEditedAttributes(null);
      setAttributeHistory([]);
      return;
    }

    onSave?.(editedAttributes);

    dispatch(setConvertedFeatures());

    setEditMode(false);
    setEditedAttributes(null);
    setAttributeHistory([]);
  };

  const handleToggleEdit = () => {
    if (editMode) {
      handleSave();
    } else {
      setEditedAttributes({ ...attributes });
      setAttributeHistory([]);
      setEditMode(true);
    }
  };

  const handleChange = (key: string, value: string) => {
    const fieldInfo = fieldsInfoMap?.get(key);

    if (fieldInfo) {
      value = processFieldValue(value, fieldInfo);
    }

    setEditedAttributes((prev) => {
      const base = prev ?? attributes;
      if (base[key] === value) return base;
      pushToHistory(base);
      return { ...base, [key]: value };
    });
  };

  const renderField = (key: string, value: any) => {
    const fieldInfo = fieldsInfoMap?.get(key);
    const currentValue = editedAttributes?.[key] ?? value;
    const codedValues = fieldInfo?.codedValueDict;
    const hasCodedValues = codedValues && Object.keys(codedValues).length > 0;
    const isEditable = fieldInfo?.editable ?? true;
    const isDateField = fieldInfo && isDateType(fieldInfo.type);
    const displayValue = getDisplayValue(currentValue, key, fieldsInfoMap);
    const viewDisplayValue = getDisplayValue(value, key, fieldsInfoMap);
    const dropdownOptions = hasCodedValues
      ? new Map(
          Object.entries(codedValues).map(([code, name]) => [
            isNaN(Number(code)) ? code : Number(code),
            name,
          ]),
        )
      : new Map();

    return (
      <Fragment key={key}>
        <span className={styles.label}>{key}:</span>
        {editMode ? (
          hasCodedValues ? (
            <Dropdown
              options={dropdownOptions}
              value={currentValue}
              onChange={(selectedValue) => handleChange(key, selectedValue)}
              disabled={!isEditable}
            />
          ) : isDateField ? (
            <MaskedInput
              maskOptions={{
                mask: "00-00-0000",
                placeholderChar: "_",
                validate: validateDate,
              }}
              value={displayValue}
              onChange={(value) => {
                handleChange(key, value);
              }}
              disabled={!isEditable}
              classes={{ input: styles.input }}
            />
          ) : (
            <Input
              ref={(el) => (inputRefs.current[key] = el)}
              classes={{ input: styles.input }}
              value={displayValue}
              onChange={(value) => handleChange(key, value)}
              disabled={!isEditable}
              onKeyDown={(e) =>
                e.ctrlKey && e.code === "KeyZ" && e.preventDefault()
              }
              maxLength={fieldInfo?.maxLength}
            />
          )
        ) : (
          <span className={styles.value}>{viewDisplayValue}</span>
        )}
      </Fragment>
    );
  };

  const canEdit = userCanEditAttributes && typeof onSave === "function";

  return (
    <div className={styles.editAttributesModal}>
      <div className={styles.header}>
        <div className={styles.leftGroup}>
          <button onClick={toggleCollapse}>
            <img
              src={chevron}
              alt={t("edit-attributes-modal.chevronAlt")}
              className={`${styles.chevron} ${!isCollapsed ? styles.rotated : ""}`}
            />
          </button>
          <div className={styles.title}>{layerName}</div>
        </div>
        <div className={styles.rightGroup}>
          {canEdit && (
            <button className={styles.editBtn} onClick={handleToggleEdit}>
              <span className={styles.editIcon} />
              {editMode
                ? t("edit-attributes-modal.saveAttributes")
                : t("edit-attributes-modal.editAttributes")}
            </button>
          )}
          <button onClick={onCancel} className={styles.closeBtn}>
            <img src={closeIcon} alt={t("edit-attributes-modal.closeAlt")} />
          </button>
        </div>
      </div>
      {!isCollapsed && (
        <CustomScrollbar height="100vh">
          <div className={styles.attributesList}>
            {Object.entries(attributes)
              .filter(([key]) => key !== "uuid")
              .map(([key, value]) => renderField(key, value))}
          </div>
        </CustomScrollbar>
      )}
    </div>
  );
};

export default EditAttributesModalView;
